import { pageTitle } from '@palaccount/config';
import type { PartnerResponse } from '@palaccount/contracts';
import { Loader2, Pencil, Plus, Power } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { PartnerStatusChip, PartnerTypeChips } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { usePartner, usePartnerActive } from './api';
import { balanceLines } from './partner-balance-lines';
import { PartnerBranchesCard } from './partner-branches-card';
import { PartnerStatementCard } from './partner-statement-card';
import { partnerMethodKey } from './constants';

function Row({ label, value, ltr }: { label: string; value: string | null; ltr?: boolean }) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value ? ltr ? <Ltr value={value} /> : value : '—'}</dd>
    </div>
  );
}

function addressLines(
  partner: PartnerResponse,
  side: 'billing' | 'shipping',
): string {
  const parts =
    side === 'billing'
      ? [partner.billingStreet, partner.billingCity, partner.billingRegion, partner.billingPostalCode, partner.billingCountry]
      : [partner.shippingStreet, partner.shippingCity, partner.shippingRegion, partner.shippingPostalCode, partner.shippingCountry];
  return parts.filter(Boolean).join(', ');
}


/**
 * MD-01 §4 — the partner record: header with code, name, type chips and the
 * worst-first status chip; the money strip; the spec's tab content as
 * stacked sections; the CRM-04 statement (POSTED documents only, R9).
 * Deactivate, never delete (MD-RULE-1).
 */
export function PartnerDetailPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { partnerId } = useParams<{ partnerId: string }>();
  const { data: partner, isPending, isError } = usePartner(partnerId);
  const setActive = usePartnerActive(partnerId ?? '');
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * GEN-209 (spec line 4709) names this act in its own words — "SWITCHING A
   * MASTER RECORD OFF" is the third of its seven. It asked with
   * `window.confirm`, which BR-PERM-15 (spec line 35168) forbids: browser
   * chrome is neither of §16.5.3's two patterns.
   *
   * Only the OFF direction asks. Switching back on restores a pickable
   * record and takes nothing away, which is why the old code passed `null`
   * for that direction and skipped the question — kept exactly.
   */
  const [offAsk, setOffAsk] = useState(false);

  // The duplicate-name warning the write responses carry ("warns and then
  // saves") — handed across by the form via navigation state, shown once.
  const warnings: string[] = (location.state as { warnings?: string[] } | null)?.warnings ?? [];

  useEffect(() => {
    if (partner) document.title = pageTitle(partner.name);
  }, [partner]);

  if (isPending) return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  if (isError || !partner) {
    return (
      <p role="alert" className="py-12 text-center text-sm text-destructive">
        {t('common.loadFailed')}
      </p>
    );
  }

  /** The act itself, once it has been agreed to (or needed no agreement). */
  const applyActive = async () => {
    setActionError(null);
    try {
      await setActive.mutateAsync(partner.isActive ? 'deactivate' : 'reactivate');
    } catch (error) {
      // The cause rides along: a structured refusal (D46 — owesOnSwitchOff /
      // weOweOnSwitchOff, naming the partner and the live balance) carries
      // key+params for the Arabic reader.
      setActionError(
        localizeServerMessage(t, error instanceof Error ? error.message : String(error), error),
      );
    }
  };

  const toggleActive = async () => {
    setActionError(null);
    // MD-01 §6: Switch off / Switch on, after a confirmation. GEN-208 — the
    // click only OPENS the dialog; nothing happens until its verb is pressed.
    if (partner.isActive) {
      setOffAsk(true);
      return;
    }
    await applyActive();
  };

  return (
    <div className="grid gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          <bdi dir="ltr">{partner.code}</bdi>
        </span>
        <h2 className="text-lg font-bold">{partner.name}</h2>
        <PartnerTypeChips partner={partner} />
        <PartnerStatusChip status={partner.status} />
        {/* MD-01 §7: the bounced-cheque marker sits ALONGSIDE the status chip. */}
        {partner.bouncedCheque ? (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
            {t('partners.bouncedCheque')}
          </span>
        ) : null}
        <div className="ms-auto flex flex-wrap gap-2">
          {/* An inactive partner, or a supplier, has nothing to invoice. */}
          {partner.isActive && partner.isCustomer ? (
            <Gated permission="sales.create" reasonKey="perm.cannot.createInvoices">
              <Button asChild variant="outline">
                <Link to={`/sales/new?partnerId=${partner.id}`}>
                  <Plus aria-hidden /> {t('partners.newInvoice')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          <GatedGroup permission="crm.edit" reasonKey="perm.cannot.editPartners">
            <>
              <Button asChild variant="outline">
                <Link to={`/partners/${partner.id}/edit`}>
                  <Pencil aria-hidden /> {t('common.edit')}
                </Link>
              </Button>
              <Button
                variant={partner.isActive ? 'destructive' : 'secondary'}
                onClick={() => void toggleActive()}
                disabled={setActive.isPending}
              >
                {setActive.isPending ? <Loader2 className="animate-spin" aria-hidden /> : <Power aria-hidden />}
                {partner.isActive ? t('partners.switchOff') : t('partners.switchOn')}
              </Button>
            </>
          </GatedGroup>
        </div>
      </div>

      {warnings.map((warning) => (
        <p
          key={warning}
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {warning}
        </p>
      ))}
      {actionError ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {actionError}
        </p>
      ) : null}

      {/*
        The old single string carried the question and the consequence in one
        sentence, which is exactly GEN-205's two slots run together. Split:
        the question is the title, "they cannot be picked on any new document"
        is the body that says what will happen. The verb on the button is
        `partners.switchOff` — the same words as the control that raised it,
        and never OK (GEN-206).

        No "cannot be undone" clause: `partners.switchOn` exists two lines
        away, so the act is reversible and the sentence would be a lie.
      */}
      <ConfirmBox
        open={offAsk}
        title={t('partners.confirmOffTitle', { name: partner.name })}
        body={t('partners.confirmOffBody')}
        confirmLabel={t('partners.switchOff')}
        busy={setActive.isPending}
        onConfirm={() => {
          setOffAsk(false);
          void applyActive();
        }}
        onCancel={() => setOffAsk(false)}
      />

      {/* BR-MD-01-09a: at 80% of the credit limit the screen shows an amber
          bar — GEN-155's amber strip, a warning to a human that blocks
          nothing. BR-MD-01-09b: past the limit (enforcement not None) it is a
          red bar with a banner NAMING THE EXCESS; with enforcement None the
          server sends no red bar even while over, so suppression needs no
          logic here. Which bar to draw is the server's verdict (creditBar) —
          the effective limit is its fact, not this screen's. */}
      {partner.creditBar === 'amber' ? (
        <p
          role="status"
          data-testid="credit-bar"
          data-level="amber"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {t('partners.creditNearLimit')}
        </p>
      ) : null}
      {partner.creditBar === 'red' ? (
        <p
          role="alert"
          data-testid="credit-bar"
          data-level="red"
          className="flex flex-wrap items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          <span>{t('partners.creditOverLimit')}</span>
          {typeof partner.creditExcess === 'string' ? (
            <Amount value={partner.creditExcess} currency={partner.currency} />
          ) : null}
        </p>
      ) : null}

      {/* The money strip of §4: owed to us — and, for a supplier, what we
          owe them — ONE FIGURE PER CURRENCY (owner decision, 30 Oct 2026),
          the partner's own currency first, never a mixed sum. */}
      <Card>
        <CardContent className="flex flex-wrap gap-8">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('partners.colOwedToUs')}
            </p>
            <p
              data-testid="partner-owed-to-us"
              className={cn(
                'flex flex-col text-xl font-bold',
                partner.status === 'over_limit' && 'text-destructive',
              )}
            >
              {balanceLines(partner, 'customer').map((line) => (
                <Amount key={line.currency} value={line.value} currency={line.currency} />
              ))}
            </p>
          </div>
          {partner.isSupplier ? (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('partnerChip.weOwe')}
              </p>
              <p data-testid="partner-owed-by-us" className="flex flex-col text-xl font-bold">
                {balanceLines(partner, 'supplier').map((line) => (
                  <Amount key={line.currency} value={line.value} currency={line.currency} />
                ))}
              </p>
            </div>
          ) : null}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('partners.fOpeningBalance')}
            </p>
            <p className="text-xl font-bold">
              <Amount value={partner.openingBalance} currency={partner.currency} />
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('partners.secGeneral')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 sm:grid-cols-2">
              {/* MD-01 field 4 — the group's CURRENT name (one record). */}
              <Row label={t('partners.fGroup')} value={partner.partnerGroupName} />
              <Row label={t('partners.fTaxId')} value={partner.taxId} ltr />
              <Row label={t('partners.fContact')} value={partner.primaryContact} />
              <Row label={t('partners.fPhone')} value={partner.phone} ltr />
              <Row label={t('partners.fEmail')} value={partner.email} ltr />
              <Row
                label={t('partners.fActive')}
                value={partner.isActive ? t('common.yes') : t('common.no')}
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('partners.secAddresses')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4">
              <Row label={t('partners.fBilling')} value={addressLines(partner, 'billing') || null} />
              <Row
                label={t('partners.fShipping')}
                value={
                  partner.shippingSameAsBilling
                    ? t('partners.sameAsBilling')
                    : addressLines(partner, 'shipping') || null
                }
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('partners.secFinancial')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 sm:grid-cols-2">
              <Row label={t('partners.fCurrency')} value={partner.currency} ltr />
              {/* MD-01 field 22 — the MD-09 term's CURRENT name (one record). */}
              <Row label={t('partners.fTerms')} value={partner.paymentTermName} />
              <Row label={t('partners.fCreditLimit')} value={partner.creditLimit} ltr />
              <Row
                label={t('partners.fEnforcement')}
                value={t(
                  partner.creditEnforcement === 'block'
                    ? 'partners.enfBlock'
                    : partner.creditEnforcement === 'warn'
                      ? 'partners.enfWarn'
                      : 'partners.enfNone',
                )}
              />
              <Row label={t('partners.fDiscount')} value={t('unit.percent', { value: partner.discountPct })} ltr />
              {/* MD-01 field 26 — the tier's CURRENT name (BR-MD-14-05). */}
              <Row label={t('partners.fPriceTier')} value={partner.priceTierName} />
              <Row
                label={t('partners.fPaymentMethod')}
                value={
                  partner.paymentMethod
                    ? (() => {
                        const key = partnerMethodKey(partner.paymentMethod);
                        return key ? t(key) : partner.paymentMethod;
                      })()
                    : null
                }
              />
              {partner.isSupplier ? (
                <Row
                  label={t('partners.fLeadTime')}
                  value={partner.leadTimeDays === null ? null : String(partner.leadTimeDays)}
                  ltr
                />
              ) : null}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('partners.secNotes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">
              {partner.notes || t('partners.notesEmpty')}
            </p>
          </CardContent>
        </Card>

        {/* INV-F3 — the branches a sales document may be addressed to. */}
        <PartnerBranchesCard partnerId={partner.id} readOnly />
      </div>

      {/*
        MD-01's record is shown HERE and not on the list: the list is the
        index, this is the record. Repeating four facts down two thousand
        rows would bury the fields a user actually scans by.
      */}
      <AuthorshipLine record={partner} />

      {/*
        CRM-04 — the Customers & Suppliers Statement, in full: §4's stacked
        cards, §5d's five summary figures, §5e's five aging bands, the search
        box of §5a field 6, the From/To period of fields 3 and 4, and §5c's
        two views. It lives in its own file because it is a SCREEN's worth of
        rules; MD-01's partner detail is where it is reached from.
      */}
      <PartnerStatementCard partner={partner} />
    </div>
  );
}
